import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import GoogleReviewsCTA from "@/components/GoogleReviewsCTA";

function Stars({ value, size = 16, className = "" }) {
  return (
    <div className={`flex items-center gap-0.5 ${className}`} aria-label={`${value} su 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= value ? "fill-white text-white" : "text-neutral-700"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1.5" data-testid="review-star-picker">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          type="button"
          key={i}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)}
          className="transition-transform duration-200 hover:scale-110"
          data-testid={`review-star-${i}`}
          aria-label={`${i} stelle`}
        >
          <Star
            size={30}
            className={i <= (hover || value) ? "fill-white text-white" : "text-neutral-700"}
            strokeWidth={1.3}
          />
        </button>
      ))}
    </div>
  );
}

/**
 * Slider orizzontale a scorrimento continuo delle recensioni.
 * - Duplica la lista per creare un ciclo infinito senza salti visibili.
 * - Sfumatura ai bordi (mask-image) per un effetto "entrano/escono in dissolvenza".
 * - Pausa in hover su desktop; touch-friendly su mobile.
 * - Velocità proporzionale al numero di card (≈ 8 secondi per card).
 */
function ReviewsMarquee({ reviews, lang }) {
  const items = [...reviews, ...reviews]; // ciclo continuo
  const durationSec = Math.max(20, reviews.length * 8);
  return (
    <div className="relative overflow-hidden" data-testid="reviews-marquee">
      <div className="reviews-marquee-mask">
        <div
          className="reviews-marquee-track"
          style={{ animationDuration: `${durationSec}s` }}
        >
          {items.map((r, i) => (
            <div
              key={`${r.id}-${i}`}
              className="reviews-marquee-item bg-[#0a0a0a] border border-white/10 p-6"
              aria-hidden={i >= reviews.length ? "true" : undefined}
            >
              <div className="flex items-center justify-between mb-3 gap-3">
                <span className="font-serif text-lg text-white truncate">{r.nome}</span>
                <Stars value={r.rating} size={13} />
              </div>
              <p className="text-sm text-neutral-400 leading-relaxed line-clamp-5">{r.commento}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 label-eyebrow text-neutral-600 text-center">
        {reviews.length} {lang === "en" ? "more reviews scrolling" : "altre recensioni in scorrimento"}
      </div>
    </div>
  );
}

export default function ReviewsSection() {
  const { lang } = useLang();
  const [reviews, setReviews] = useState([]);
  const [nome, setNome] = useState("");
  const [rating, setRating] = useState(0);
  const [commento, setCommento] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/reviews").then((r) => setReviews(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const average = useMemo(() => {
    if (!reviews.length) return 0;
    return reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
  }, [reviews]);

  const submit = async (e) => {
    e.preventDefault();
    if (!nome.trim() || !commento.trim()) { toast.error(lang === "en" ? "Enter name and comment" : "Inserisci nome e commento"); return; }
    if (rating < 1) { toast.error(lang === "en" ? "Select a rating from 1 to 5 stars" : "Seleziona un voto da 1 a 5 stelle"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", { nome: nome.trim(), rating, commento: commento.trim() });
      toast.success(lang === "en" ? "Thank you for your review!" : "Grazie per la tua recensione!");
      setNome(""); setRating(0); setCommento("");
      await load();
    } catch {
      toast.error(lang === "en" ? "Error submitting the review" : "Errore nell'invio della recensione");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="recensioni" className="relative py-32 sm:py-48 px-6 sm:px-10 lg:px-14 max-w-[1600px] mx-auto" data-testid="reviews-section">
      <div className="flex items-center gap-4 mb-8">
        <span className="w-10 h-px bg-white/40" />
        <span className="label-eyebrow text-white/70">{lang === "en" ? "Reviews" : "Recensioni"}</span>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1 }}
          className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.0] tracking-tight text-white"
        >
          {lang === "en" ? "The word" : "La parola"}<br />
          <span className="italic text-neutral-500">{lang === "en" ? "to our clients." : "ai clienti."}</span>
        </motion.h2>
        {reviews.length > 0 && (
          <div className="flex items-center gap-5" data-testid="reviews-average">
            <span className="font-sans font-light text-6xl text-white">{average.toFixed(1)}</span>
            <div>
              <Stars value={Math.round(average)} size={18} />
              <div className="label-eyebrow text-neutral-500 mt-2">{reviews.length} {lang === "en" ? "reviews" : "recensioni"}</div>
            </div>
          </div>
        )}
      </div>

      <GoogleReviewsCTA variant="full" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Colonna sinistra: 2 recensioni in evidenza + slider infinito */}
        <div className="lg:col-span-7 flex flex-col gap-10">
          {reviews.length === 0 ? (
            <div className="bg-[#0a0a0a] p-12 text-center border border-white/10">
              <p className="font-serif italic text-2xl text-neutral-400">{lang === "en" ? "Be the first to leave a review." : "Sii il primo a lasciare una recensione."}</p>
            </div>
          ) : (
            <>
              {/* Le 2 recensioni più recenti — sempre visibili, in evidenza */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="reviews-pinned">
                {reviews.slice(0, 2).map((r, i) => (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: i * 0.1 }}
                    className="bg-[#0a0a0a] border border-white/10 p-7 sm:p-8 flex flex-col"
                    data-testid={`review-pinned-${r.id}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-serif text-xl text-white truncate pr-3">{r.nome}</span>
                      <Stars value={r.rating} size={14} />
                    </div>
                    <p className="text-neutral-400 leading-relaxed">{r.commento}</p>
                  </motion.div>
                ))}
              </div>

              {/* Slider infinito con le restanti recensioni */}
              {reviews.length > 2 && (
                <ReviewsMarquee reviews={reviews.slice(2)} lang={lang} />
              )}
            </>
          )}
        </div>

        {/* Form */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5 border border-white/10 p-8 sm:p-10 h-fit lg:sticky lg:top-28"
          data-testid="review-form"
        >
          <h3 className="font-serif text-3xl text-white mb-8">{lang === "en" ? "Leave your review" : "Lascia la tua recensione"}</h3>

          <label className="label-eyebrow text-neutral-500 block mb-2">{lang === "en" ? "Name" : "Nome"}</label>
          <input
            type="text" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={80}
            placeholder={lang === "en" ? "Your name" : "Il tuo nome"}
            className="w-full bg-transparent border border-white/10 px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-white/40 mb-6"
            data-testid="review-name-input"
          />

          <label className="label-eyebrow text-neutral-500 block mb-3">{lang === "en" ? "Rating" : "Voto"}</label>
          <div className="mb-6"><StarPicker value={rating} onChange={setRating} /></div>

          <label className="label-eyebrow text-neutral-500 block mb-2">{lang === "en" ? "Comment" : "Commento"}</label>
          <textarea
            value={commento} onChange={(e) => setCommento(e.target.value)} rows={4} maxLength={1000}
            placeholder={lang === "en" ? "Tell us about your experience" : "Racconta la tua esperienza"}
            className="w-full bg-transparent border border-white/10 px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-white/40 mb-8"
            data-testid="review-comment-input"
          />

          <button type="submit" disabled={busy} className="btn-magnetic btn-solid w-full justify-center disabled:opacity-50" data-testid="review-submit-btn">
            {busy ? (lang === "en" ? "Sending…" : "Invio…") : (lang === "en" ? "Publish review" : "Pubblica recensione")}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
